import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Building2, Plus } from 'lucide-react';
import type { Organization } from '../../generated/api/types.gen.js';
import { listOrganizationsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import { Badge, Button, Callout, DataTable, EmptyState, SearchBar, Toolbar } from '../../components/ui/index.js';
import type { DataTableColumn } from '../../components/ui/index.js';
import { ListPage } from '../../components/page/ListPage.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { Can } from '../../components/auth/Can.js';
import { PAGE_HELP } from '../../help/index.js';
import { orgPlanBadgeVariant, orgStatusBadgeVariant } from './org-badges.js';
import { formatDate } from './user-labels.js';

const sentenceCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export const OrganizationsPage: React.FC = () => {
  const help = PAGE_HELP.organizations;
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { data, isPending, isError, refetch } = useQuery(listOrganizationsOptions());

  const needle = search.trim().toLowerCase();
  const organizations = (data?.organizations ?? []).filter(
    (org) => !needle || org.name.toLowerCase().includes(needle) || org.slug.toLowerCase().includes(needle),
  );

  const newOrgLink = (
    <Can policy="CanCreateOrgs">
      <Link
        to="/admin/organizations/new"
        className={buttonStyles({ variant: 'primary', appearance: 'fill', size: 'sm' })}
      >
        <Plus size={14} aria-hidden="true" />
        New organization
      </Link>
    </Can>
  );

  const columns: DataTableColumn<Organization>[] = [
    {
      key: 'name',
      header: 'Organization',
      sortable: true,
      sortValue: (org) => org.name.toLowerCase(),
      render: (org) => (
        <div className="min-w-0">
          <Link
            to={`/admin/organizations/${org.id}`}
            className="block truncate text-sm font-medium text-heading hover:text-accent-text"
            onClick={(e) => e.stopPropagation()}
          >
            {org.name}
          </Link>
          <span className="block truncate font-mono text-xs text-muted">{org.slug}</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (org) => (
        <Badge variant={orgStatusBadgeVariant(org.status)} dot>
          {sentenceCase(org.status)}
        </Badge>
      ),
    },
    {
      key: 'plan',
      header: 'Plan',
      render: (org) => <Badge variant={orgPlanBadgeVariant(org.plan)}>{sentenceCase(org.plan)}</Badge>,
    },
    {
      key: 'owner',
      header: 'Owner',
      render: (org) => <span className="text-sm text-body">{org.ownerName ?? 'No owner'}</span>,
    },
    {
      key: 'members',
      header: 'Members',
      align: 'right',
      sortable: true,
      sortValue: (org) => org.memberCount,
      render: (org) => <span className="text-sm text-body">{org.memberCount}</span>,
    },
    {
      key: 'teams',
      header: 'Teams',
      align: 'right',
      sortable: true,
      sortValue: (org) => org.teamCount,
      render: (org) => <span className="text-sm text-body">{org.teamCount}</span>,
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      sortValue: (org) => org.createdAt,
      render: (org) => <span className="text-sm text-muted">{formatDate(org.createdAt)}</span>,
    },
  ];

  return (
    <ListPage
      title="Organizations"
      subtitle="Every tenant on the platform, with its plan, owner and size."
      helpContent={help}
      actions={newOrgLink}
      alert={
        isError ? (
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Could not load organizations"
            actions={
              <Button size="sm" variant="secondary" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Check your connection and try again.
          </Callout>
        ) : undefined
      }
      toolbar={
        <Toolbar search={<SearchBar value={search} onChange={setSearch} placeholder="Search name or slug…" />} />
      }
    >
      {!isError && (
        <DataTable
          columns={columns}
          rows={organizations}
          rowKey={(org) => org.id}
          onRowClick={(org) => navigate(`/admin/organizations/${org.id}`)}
          loading={isPending}
          aria-label="Organizations"
          empty={
            <EmptyState
              icon={Building2}
              title={help.emptyState.title}
              description={help.emptyState.description}
              learnItems={help.emptyState.learnItems}
              action={newOrgLink}
            />
          }
        />
      )}
    </ListPage>
  );
};
